import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output,
  QueryList, ViewChild, ViewChildren,
} from '@angular/core';
import { kindInfo } from '../../models/scenario-demo-data';
import { AnswerState, GradeResult, QuestionKindInfo, ScenarioChoice, ScenarioCoaching, ScenarioQuestion } from '../../models/scenario.model';
import { grade, isReady } from '../../services/scenario-grading';

type RowState = '' | 'selected' | 'correct' | 'wrong' | 'answer' | 'missed';

interface ChoiceRow { text: string; state: RowState; tag: string; }
interface TfRow { text: string; state: string; tag: string; tState: string; fState: string; }
interface ClozePart { isBlank: boolean; text: string; index: number; state: string; }
interface TermRow { text: string; used: boolean; selected: boolean; }
interface OrderRow { item: number; n: number; label: string; text: string; state: string; tag: string; }
interface MatchRow { index: number; text: string; dot?: string; state: string; pairLabel: string; }
interface SpotRow { label: string; x: number; y: number; n: number; state: string; tag: string; }
interface PhraseChip { index: number; text: string; used: boolean; state: string; }
interface ElementRow { text: string; state: string; tag: string; }
interface AirportRow { id: string; note: string; x: number; y: number; labelX: number; labelY: number; state: string; }
interface Tick { v: string; hot: boolean; }

const LETTERS = 'ABCDEFGHIJ';
const PRIORITY = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth'];
const MAP_W = 860;
const MAP_H = 360;

@Component({
  selector: 'app-scenario-question',
  templateUrl: './scenario-question.component.html',
  styleUrls: ['./scenario-question.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenarioQuestionComponent implements OnChanges, OnDestroy {
  @Input() question: ScenarioQuestion;
  @Input() stepLabel = '';
  /** Library thumbnails: no timers, no audio. */
  @Input() frozen = false;
  @Input() continueLabel = 'Continue';
  @Input() coaching?: ScenarioCoaching;
  activeCoaching: ScenarioCoaching | null = null;
  answerContinueLabel = 'Continue';
  @Output() readonly checkedChange = new EventEmitter<GradeResult>();
  @Output() readonly continued = new EventEmitter<GradeResult>();

  @ViewChild('mapSvg') mapSvg: ElementRef<SVGSVGElement>;
  @ViewChildren('orderCard') orderCards: QueryList<ElementRef<HTMLElement>>;

  info: QuestionKindInfo;
  usesChoiceList = false;
  showInlineImage = false;
  dialTransform = '';
  meta = '';
  a: AnswerState = {};
  checked = false;
  ready = false;
  result: GradeResult | null = null;
  hint = '';

  choiceRows: ChoiceRow[] = [];
  whyRows: ChoiceRow[] = [];
  stage2 = false;
  confLevels: { label: string; selected: boolean }[] = [];
  tfRows: TfRow[] = [];
  clozeParts: ClozePart[] = [];
  termRows: TermRow[] = [];
  selectedTerm: number | null = null;
  orderRows: OrderRow[] = [];
  matchLeft: MatchRow[] = [];
  matchRight: MatchRow[] = [];
  matchSel: number | null = null;
  inputState = '';
  sliderLabel = '';
  sliderState = '';
  timeLeft = 0;
  timerText = '';
  timerDash = '';
  timerUrgent = false;
  asiTicks: Tick[] = [];
  altTicks: Tick[] = [];
  hdgTicks: Tick[] = [];
  horizonTransform = '';
  vsiHeight = 0;
  vsiUp = false;
  panelCues: string[] = [];
  zoom = 1;
  readonly zoomLevels = [1, 1.6, 2.4];
  spotRows: SpotRow[] = [];
  auditFound = 0;
  auditTotal = 0;
  lineChips: PhraseChip[] = [];
  bankChips: PhraseChip[] = [];
  elementRows: ElementRow[] = [];
  playing = false;
  playsLeft = 0;
  audioProgress = 0;
  waveBars: { h: number; played: boolean }[] = [];
  airportRows: AirportRow[] = [];
  origin = { x: 0, y: 0 };
  plot = { x: 0, y: 0 };
  plotState = '';
  dragging = false;

  private rightOrder: number[] = [];
  private timer: any;
  private audioTimer: any;

  constructor(private readonly cdr: ChangeDetectorRef, private readonly zone: NgZone) { }

  ngOnChanges(): void {
    this.reset();
  }

  ngOnDestroy(): void {
    this.stopTimers();
  }

  get q(): ScenarioQuestion { return this.question; }

  reset(): void {
    this.stopTimers();
    const q = this.question;
    if (!q) { return; }
    this.info = kindInfo(q.kind);
    this.usesChoiceList = ['multiple-choice', 'confidence', 'timed', 'panel-read', 'audio-cue'].includes(q.kind);
    this.showInlineImage = !!q.imageUrl && !['chart-lookup', 'cockpit-control', 'hazard-audit'].includes(q.kind);
    this.checked = false;
    this.result = null;
    this.activeCoaching = null;
    this.answerContinueLabel = this.continueLabel;
    this.selectedTerm = null;
    this.matchSel = null;
    this.zoom = 1;
    this.a = {};
    const n = (q.items || q.pairs || []).length;
    // ponytail: fixed rotation instead of random shuffle — a derangement for any n >= 2, stable across reloads.
    const shift = Math.ceil(n / 2);
    const rotated = Array.from({ length: n }, (_, i) => (i + shift) % n);

    switch (q.kind) {
      case 'true-false-round': this.a.tf = (q.statements || []).map(() => null); break;
      case 'cloze': this.a.blanks = (q.clozeAnswers || []).map(() => null); break;
      case 'sequencing': case 'ranked': this.a.order = rotated; break;
      case 'matching': this.a.match = {}; this.rightOrder = rotated; break;
      case 'slider': this.a.value = Math.round(((q.min || 0) + (q.max || 100)) / 4); break;
      case 'multiple-response': case 'hazard-audit': this.a.multi = []; break;
      case 'radio-phraseology': this.a.line = []; break;
      case 'timed': this.timeLeft = q.seconds || 12; this.startTimer(); break;
      case 'audio-cue': this.playsLeft = q.plays || 2; this.audioProgress = 0; this.playing = false; break;
      case 'diversion-plot': {
        const o = (q.airports || []).find(p => p.origin) || { x: 0, y: 0 };
        this.origin = { x: o.x, y: o.y };
        this.plot = { x: o.x + 60, y: o.y - 60 };
        break;
      }
    }
    this.meta = this.buildMeta();
    this.refresh();
  }

  // ---------- interactions ----------

  pickChoice(i: number): void { if (!this.checked) { this.a.choice = i; this.refresh(); } }
  pickWhy(i: number): void { if (!this.checked && this.a.choice != null) { this.a.why = i; this.refresh(); } }
  pickConfidence(i: number): void { if (!this.checked) { this.a.confidence = i; this.refresh(); } }

  toggleMulti(i: number): void {
    if (this.checked) { return; }
    const m = this.a.multi || [];
    this.a.multi = m.includes(i) ? m.filter(x => x !== i) : [...m, i];
    this.refresh();
  }

  pickTf(i: number, value: boolean): void {
    if (this.checked) { return; }
    this.a.tf = (this.a.tf || []).map((v, j) => (j === i ? value : v));
    this.refresh();
  }

  pickTerm(i: number): void {
    if (this.checked || (this.a.blanks || []).includes(i)) { return; }
    this.selectedTerm = this.selectedTerm === i ? null : i;
    this.refresh();
  }

  clickBlank(i: number): void {
    if (this.checked) { return; }
    const b = [...(this.a.blanks || [])];
    if (b[i] !== null) { b[i] = null; } else if (this.selectedTerm !== null) { b[i] = this.selectedTerm; this.selectedTerm = null; }
    this.a.blanks = b;
    this.refresh();
  }

  dropTerm(event: CdkDragDrop<unknown>, blank: number): void {
    const term = event.item.data as number;
    if (this.checked || (this.a.blanks || []).includes(term)) { return; }
    const blanks = [...(this.a.blanks || [])];
    blanks[blank] = term;
    this.a.blanks = blanks;
    this.selectedTerm = null;
    this.refresh();
  }

  move(pos: number, dir: number): void {
    const o = [...(this.a.order || [])], j = pos + dir;
    if (this.checked || j < 0 || j >= o.length) { return; }
    const cards = this.orderCards ? this.orderCards.toArray() : [];
    const tops = new Map<number, number>();
    cards.forEach((card, i) => tops.set(o[i], card.nativeElement.getBoundingClientRect().top));
    cards.forEach(card => card.nativeElement.getAnimations().forEach(animation => animation.cancel()));
    [o[pos], o[j]] = [o[j], o[pos]];
    this.a.order = o;
    this.refresh();
    if (!cards.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }
    this.cdr.detectChanges();
    this.orderCards.forEach((card, i) => {
      const el = card.nativeElement;
      const distance = (tops.get(o[i]) || 0) - el.getBoundingClientRect().top;
      if (distance) {
        el.animate([{ transform: `translateY(${distance}px)` }, { transform: 'translateY(0)' }],
          { duration: 220, easing: 'cubic-bezier(0.77, 0, 0.175, 1)' });
      }
    });
  }

  dropOrder(event: CdkDragDrop<unknown>): void {
    if (this.checked || event.previousIndex === event.currentIndex) { return; }
    const order = [...(this.a.order || [])];
    order.splice(event.currentIndex, 0, order.splice(event.previousIndex, 1)[0]);
    this.a.order = order;
    this.refresh();
  }

  clickLeft(i: number): void {
    if (this.checked) { return; }
    const m = this.a.match || {};
    if (m[i] !== undefined) { const copy = { ...m }; delete copy[i]; this.a.match = copy; } else { this.matchSel = this.matchSel === i ? null : i; }
    this.refresh();
  }

  clickRight(j: number): void {
    const m = this.a.match || {};
    if (this.checked || this.matchSel === null || Object.values(m).includes(j)) { return; }
    this.a.match = { ...m, [this.matchSel]: j };
    this.matchSel = null;
    this.refresh();
  }

  dropMatch(event: CdkDragDrop<unknown>, right: number): void {
    if (this.checked) { return; }
    const left = event.item.data as number;
    const match = { ...(this.a.match || {}) };
    Object.keys(match).forEach(key => { if (match[+key] === right) { delete match[+key]; } });
    match[left] = right;
    this.a.match = match;
    this.matchSel = null;
    this.refresh();
  }

  setText(v: string): void { this.a.text = v; this.refresh(); }
  setValue(v: string): void { this.a.value = +v; this.refresh(); }
  setZoom(z: number): void { this.zoom = z; }

  addPhrase(i: number): void {
    if (this.checked || (this.a.line || []).includes(i)) { return; }
    this.a.line = [...(this.a.line || []), i];
    this.refresh();
  }

  removePhrase(k: number): void {
    if (this.checked) { return; }
    this.a.line = (this.a.line || []).filter((_, j) => j !== k);
    this.refresh();
  }

  dropPhrase(event: CdkDragDrop<unknown>): void {
    if (this.checked) { return; }
    const line = [...(this.a.line || [])];
    if (event.previousContainer === event.container) { line.splice(event.previousIndex, 1); }
    else if (line.includes(event.item.data as number)) { return; }
    line.splice(event.currentIndex, 0, event.item.data as number);
    this.a.line = line;
    this.refresh();
  }

  returnPhrase(event: CdkDragDrop<unknown>): void {
    if (this.checked || event.previousContainer === event.container) { return; }
    this.removePhrase(event.previousIndex);
  }

  pickAirport(id: string): void {
    if (this.checked) { return; }
    const ap = (this.q.airports || []).find(p => p.id === id);
    if (!ap || ap.origin) { return; }
    this.a.airport = id;
    this.plot = { x: ap.x, y: ap.y };
    this.refresh();
  }

  mapDown(e: PointerEvent): void {
    if (this.checked) { return; }
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    this.dragging = true;
  }

  mapMove(e: PointerEvent): void {
    if (!this.dragging || !this.mapSvg) { return; }
    const r = this.mapSvg.nativeElement.getBoundingClientRect();
    this.plot = { x: (e.clientX - r.left) / r.width * MAP_W, y: (e.clientY - r.top) / r.height * MAP_H };
    this.a.airport = undefined;
    this.refresh();
  }

  mapUp(): void {
    if (!this.dragging) { return; }
    this.dragging = false;
    let best: string | undefined, bd = 55;
    (this.q.airports || []).filter(p => !p.origin).forEach(p => {
      const d = Math.hypot(p.x - this.plot.x, p.y - this.plot.y);
      if (d < bd) { bd = d; best = p.id; }
    });
    if (best) { this.pickAirport(best); } else { this.refresh(); }
  }

  toggleAudio(): void {
    const synth = window.speechSynthesis;
    if (this.playing) { synth?.cancel(); this.stopAudio(); this.refresh(); return; }
    if (this.frozen || this.playsLeft <= 0) { return; }
    this.playsLeft--;
    this.playing = true;
    this.audioProgress = 0;
    const script = this.q.audioScript || this.q.title;
    // ponytail: progress is estimated at ~2.6 words/s; speechSynthesis has no reliable duration.
    const estimateMs = Math.max(3000, script.split(/\s+/).length / 2.6 * 1000);
    const started = Date.now();
    this.audioTimer = setInterval(() => {
      this.audioProgress = Math.min(0.97, (Date.now() - started) / estimateMs);
      this.buildWave();
      this.cdr.markForCheck();
    }, 200);
    if (synth) {
      const u = new SpeechSynthesisUtterance(script);
      u.rate = 1.05;
      u.onend = () => this.zone.run(() => { this.audioProgress = 1; this.stopAudio(); this.refresh(); });
      synth.cancel();
      synth.speak(u);
    }
    this.refresh();
  }

  check(): void {
    if (this.checked || !this.ready) { return; }
    this.stopTimers();
    this.checked = true;
    this.result = grade(this.q, this.a);
    this.activeCoaching = this.result.correctness !== 'ok' ? this.coaching || null : null;
    this.answerContinueLabel = this.activeCoaching?.after === 'retry' ? 'Try again' : this.continueLabel;
    this.refresh();
    this.checkedChange.emit(this.result);
  }

  primary(): void {
    if (this.checked && this.result) { this.continued.emit(this.result); } else { this.check(); }
  }

  trackByIndex(i: number): number { return i; }
  trackByOrderItem(_i: number, row: OrderRow): number { return row.item; }

  // ---------- view models ----------

  private refresh(): void {
    const q = this.q, a = this.a, checked = this.checked;
    this.ready = isReady(q, a);
    this.hint = checked ? '' : this.buildHint();

    const choiceKinds = ['multiple-choice', 'answer-rationale', 'confidence', 'timed', 'panel-read', 'audio-cue'];
    if (choiceKinds.includes(q.kind)) {
      this.choiceRows = (q.choices || []).map((c, i) => this.radioRow(c, a.choice === i));
    }
    switch (q.kind) {
      case 'answer-rationale':
        this.stage2 = a.choice != null;
        this.whyRows = (q.whys || []).map((c, i) => this.radioRow(c, a.why === i));
        break;
      case 'confidence':
        this.confLevels = ['Guessing', 'Fairly sure', 'Certain'].map((label, i) => ({ label, selected: a.confidence === i }));
        break;
      case 'multiple-response':
        this.choiceRows = (q.choices || []).map((c, i) => {
          const sel = (a.multi || []).includes(i);
          if (!checked) { return { text: c.text, state: sel ? 'selected' : '', tag: '' } as ChoiceRow; }
          if (sel) { return { text: c.text, state: c.correct ? 'correct' : 'wrong', tag: c.correct ? '+1' : '−1' } as ChoiceRow; }
          return { text: c.text, state: c.correct ? 'missed' : '', tag: c.correct ? 'Missed' : '' } as ChoiceRow;
        });
        break;
      case 'true-false-round':
        this.tfRows = (q.statements || []).map((s, i) => {
          const v = (a.tf || [])[i], right = v === s.answer;
          const btn = (b: boolean) => (v !== b ? '' : !checked ? 'selected' : b === s.answer ? 'correct' : 'wrong');
          return { text: s.text, state: checked ? (right ? 'ok' : 'bad') : '', tag: checked && !right ? `Answer: ${s.answer ? 'True' : 'False'}` : '',
            tState: btn(true), fState: btn(false) };
        });
        break;
      case 'cloze': {
        const blanks = a.blanks || [];
        this.clozeParts = (q.clozeParts || []).map(p => {
          if (typeof p === 'string') { return { isBlank: false, text: p, index: -1, state: '' }; }
          const val = blanks[p], filled = val !== null && val !== undefined;
          const state = checked && filled ? (val === (q.clozeAnswers || [])[p] ? 'correct' : 'wrong')
            : filled ? 'filled' : this.selectedTerm !== null ? 'target' : '';
          return { isBlank: true, text: filled ? (q.terms || [])[val as number] : 'drop a term', index: p, state };
        });
        this.termRows = (q.terms || []).map((t, i) => ({ text: t, used: blanks.includes(i), selected: this.selectedTerm === i }));
        break;
      }
      case 'sequencing':
      case 'ranked':
        this.orderRows = (a.order || []).map((item, pos) => {
          const ok = item === pos;
          const label = q.kind === 'ranked' ? PRIORITY[pos] || `#${pos + 1}` : '';
          const should = q.kind === 'ranked' ? (PRIORITY[item] || '').toLowerCase() : String(item + 1);
          return { item, n: pos + 1, label, text: (q.items || [])[item], state: checked ? (ok ? 'ok' : 'bad') : '', tag: checked && !ok ? `Should be ${should}` : '' };
        });
        break;
      case 'matching': {
        const m = a.match || {};
        const pairOrder = Object.keys(m).map(Number);
        const letterFor = (left: number) => LETTERS[pairOrder.indexOf(left)] || '';
        this.matchLeft = (q.pairs || []).map((p, i) => {
          const paired = m[i] !== undefined;
          const state = checked ? (m[i] === i ? 'correct' : 'wrong') : this.matchSel === i ? 'selected' : paired ? 'paired' : '';
          return { index: i, text: p.left, dot: p.dot, state, pairLabel: paired ? letterFor(i) : '' };
        });
        this.matchRight = this.rightOrder.map(j => {
          const owner = pairOrder.find(l => m[l] === j);
          const taken = owner !== undefined;
          const state = checked && taken ? (owner === j ? 'correct' : 'wrong') : taken ? 'paired' : this.matchSel !== null ? 'target' : '';
          return { index: j, text: (q.pairs || [])[j].right, state, pairLabel: taken ? letterFor(owner as number) : '' };
        });
        break;
      }
      case 'numeric':
      case 'chart-lookup':
        this.inputState = checked ? this.result?.correctness === 'ok' ? 'correct' : 'wrong' : '';
        break;
      case 'slider':
        this.sliderLabel = q.unit === '°' ? String(a.value).padStart(3, '0') + '°' : `${a.value} ${q.unit || ''}`;
        this.dialTransform = `rotate(${((a.value as number) - (q.min || 0)) / ((q.max || 100) - (q.min || 0)) * 360}deg)`;
        this.sliderState = checked ? this.result?.correctness === 'ok' ? 'correct' : 'wrong' : '';
        break;
      case 'timed':
        this.buildTimer();
        break;
      case 'panel-read':
        this.buildPanel();
        break;
      case 'cockpit-control':
        this.spotRows = (q.hotspots || []).map((h, i) => ({ ...this.radioRow({ text: h.label, correct: h.correct }, a.choice === i), label: h.label, x: h.x, y: h.y, n: i + 1 }));
        break;
      case 'hazard-audit': {
        const sel = a.multi || [];
        this.auditTotal = (q.hotspots || []).filter(h => h.correct).length;
        this.auditFound = sel.filter(i => (q.hotspots || [])[i]?.correct).length;
        this.spotRows = (q.hotspots || []).map((h, i) => {
          const on = sel.includes(i);
          let state: RowState = on ? 'selected' : '', tag = '';
          if (checked && on) { state = h.correct ? 'correct' : 'wrong'; tag = h.correct ? 'found' : 'false positive'; }
          if (checked && !on && h.correct) { state = 'missed'; tag = 'missed'; }
          return { label: h.label, x: h.x, y: h.y, n: i + 1, state, tag };
        });
        break;
      }
      case 'radio-phraseology': {
        const line = a.line || [], phrases = q.phrases || [];
        this.lineChips = line.map(i => ({ index: i, text: phrases[i].text, used: true, state: checked ? (phrases[i].element < 0 ? 'wrong' : 'correct') : '' }));
        this.bankChips = phrases.map((p, i) => ({ index: i, text: p.text, used: line.includes(i), state: '' }));
        this.elementRows = (q.elements || []).map((t, ei) => {
          const present = line.some(i => phrases[i].element === ei);
          return { text: t, state: checked ? (present ? 'ok' : 'bad') : '', tag: checked ? (present ? 'included' : 'missing') : '' };
        });
        break;
      }
      case 'audio-cue':
        this.buildWave();
        break;
      case 'diversion-plot':
        this.plotState = checked ? (this.result?.correctness === 'ok' ? 'correct' : 'wrong') : '';
        this.airportRows = (q.airports || []).map(p => {
          const left = p.x > MAP_W - 200;
          return { id: p.id, note: p.note, x: p.x, y: p.y, labelX: (p.x + (left ? -16 : 16)) / MAP_W * 100, labelY: (p.y - 4) / MAP_H * 100,
            state: (a.airport === p.id ? (checked ? (p.id === q.correctAirport ? 'correct' : 'wrong') : 'selected') : '') + (left ? ' flip' : '') };
        });
        break;
    }
    this.cdr.markForCheck();
  }

  private radioRow(c: ScenarioChoice, selected: boolean): ChoiceRow {
    const checked = this.checked;
    if (!checked) { return { text: c.text, state: selected ? 'selected' : '', tag: '' }; }
    if (selected) { return { text: c.text, state: c.correct ? 'correct' : 'wrong', tag: c.correct ? 'Correct' : 'Your answer' }; }
    return { text: c.text, state: c.correct ? 'answer' : '', tag: c.correct ? 'Correct answer' : '' };
  }

  private buildMeta(): string {
    const q = this.q, pts = `${q.points} pts`;
    switch (q.kind) {
      case 'multiple-response': return `Partial credit · ${pts}`;
      case 'true-false-round': return `${(q.statements || []).length} statements · ${pts}`;
      case 'cloze': return `${(q.clozeAnswers || []).length} blanks · ${pts}`;
      case 'numeric': case 'slider': return `±${q.tolerance}${q.unit === '°' ? '°' : ' ' + (q.unit || '')} · ${pts}`;
      case 'timed': return `${q.seconds || 12} s · ${pts}`;
      case 'audio-cue': return `${q.plays || 2} plays · ${pts}`;
      case 'hazard-audit': return `${(q.hotspots || []).filter(h => h.correct).length} hazards · found / missed / false positive`;
      default: return pts;
    }
  }

  private buildHint(): string {
    const hints: { [k: string]: string } = {
      'multiple-response': 'Select every item that applies.', 'true-false-round': 'Answer every statement to submit.',
      'cloze': 'Fill every blank to submit.', 'answer-rationale': this.a.choice == null ? 'Choose an action first.' : 'Now choose the rationale.',
      'confidence': this.a.choice == null ? 'Select an answer.' : 'Rate your confidence to submit.',
      'sequencing': 'Drag to reorder, or use the arrows.', 'ranked': 'Drag to reorder, or use the arrows.',
      'matching': 'Pair every item to submit.', 'numeric': `Enter a value in ${this.q.unit || 'units'}.`,
      'slider': 'Drag the slider, then submit.', 'timed': 'Commit before the clock runs out.',
      'panel-read': 'Read every tape before you commit.', 'chart-lookup': 'Read the value off the chart.',
      'cockpit-control': 'Click one control.', 'radio-phraseology': 'Drag or tap phrases to build the call.',
      'audio-cue': 'Play the clip, then answer.', 'hazard-audit': 'Tag everything you would call out.',
      'diversion-plot': 'Drag the endpoint to an alternate, or tap an airport.',
    };
    return hints[this.q.kind] || 'Select an answer.';
  }

  private startTimer(): void {
    if (this.frozen) { return; }
    this.timer = setInterval(() => {
      if (this.timeLeft <= 1) {
        this.timeLeft = 0;
        this.a.expired = true;
        this.ready = true;
        this.check();
        return;
      }
      this.timeLeft--;
      this.refresh();
    }, 1000);
  }

  private buildTimer(): void {
    const total = this.q.seconds || 12;
    const expired = !!this.a.expired && this.a.choice == null;
    this.timerUrgent = this.timeLeft <= 4;
    this.timerText = expired ? 'Time expired' : `00:${String(this.timeLeft).padStart(2, '0')}`;
    this.timerDash = `${(this.timeLeft / total * 100.5).toFixed(1)} 100.5`;
  }

  private buildPanel(): void {
    const p = this.q.panel || { ias: 100, alt: 3000, vsi: 0, pitch: 0, bank: 0, hdg: 360 };
    const tape = (center: number, step: number) => [2, 1, 0, -1, -2].map(k => ({ v: String(Math.round(center / step) * step + k * step), hot: false }));
    this.asiTicks = tape(p.ias, 10);
    this.altTicks = tape(p.alt, 100);
    const hdg = Math.round(p.hdg / 10) * 10;
    this.hdgTicks = [-30, -20, -10, 0, 10, 20, 30].map(d => ({ v: String((hdg + d + 360) % 360 || 360).padStart(3, '0'), hot: d === 0 }));
    this.horizonTransform = `rotate(${-p.bank}deg) translateY(${p.pitch * 7}px)`;
    this.vsiUp = p.vsi > 0;
    this.vsiHeight = Math.min(45, Math.abs(p.vsi) / 2000 * 45);
    this.panelCues = [
      `Bank ${Math.abs(p.bank)}° ${p.bank >= 0 ? 'right' : 'left'}`,
      `Pitch ${Math.abs(p.pitch)}° nose ${p.pitch >= 0 ? 'up' : 'down'}`,
      `VSI ${p.vsi > 0 ? '+' : ''}${p.vsi} fpm`,
      `Heading ${String(p.hdg).padStart(3, '0')}`,
    ];
  }

  private buildWave(): void {
    this.waveBars = Array.from({ length: 48 }, (_, i) => ({
      h: Math.round(8 + Math.abs(Math.sin(i * 0.7) * Math.cos(i * 0.23)) * 34),
      played: i / 48 < this.audioProgress,
    }));
  }

  private stopAudio(): void {
    clearInterval(this.audioTimer);
    this.playing = false;
  }

  private stopTimers(): void {
    clearInterval(this.timer);
    if (this.playing) { window.speechSynthesis?.cancel(); }
    this.stopAudio();
  }
}
